import React, { FC, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { DateTime } from 'luxon';
import { useSelector, useDispatch, useLogger, useI18n, useButton, useApi } from '~/Hooks';
import _ from 'lodash';
import * as actions from '~actions';
import { useStorage } from '~Components/Storage';
import usePrintReceipt from '~Hooks/usePrintReceipt';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import { Box, BlueButton, Button, TransparentButton } from '~Styles';
import ReceiptIconSrc from '~Assets/icons/receipt.svg';
import EmailReceiptModal from './EmailReceiptModal';
import { SEARCH_ORDERS_FOR_EMAIL_QUERY } from './queries';
import {
  AdvancedSearchOrdersForEmail,
  AdvancedSearchOrdersForEmailVariables,
} from './__queries__/AdvancedSearchOrdersForEmail';
import DividerWithText from '~Components/DividerWithText';
import Icon from '~ComponentsImproved/Icon';
import MailIconSrc from '~Assets/icons/mail.svg';
import styled from '@emotion/styled';

const MailButton = styled(TransparentButton)`
  border: 1px solid var(--colorBlueGray200);
`;

/** How many times we retry the order search, and how long we wait between each try. */
const FIND_ORDER_MAX_ATTEMPTS = 4;
const FIND_ORDER_RETRY_DELAY_MS = 6000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_PRINTED_IDS = 10;

interface Props {
  venueId: string;
}

const ReceiptPrinter: FC<Props> = ({ venueId }) => {
  const print = useSelector((state) => state.POSReceiptPrints);
  const POSOrder = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));
  const api = useApi();
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const [storage] = useStorage();
  const { printReceipt } = usePrintReceipt({ venueId });

  const askToPrintModal = useModal();
  const emailReceiptModal = useModal();

  // Set to true when the user confirms the print (yes/no)
  const [printConfirmed, setPrintConfirmed] = useState(!storage.askToPrint);
  const printedIdsRef = useRef<string[]>([]);

  useEffect(() => setPrintConfirmed(!storage.askToPrint), [storage.askToPrint]);

  const { i18n } = useI18n();

  const logger = useLogger('receipt-printer');

  // If we sync the order before printing, POSPayment and POSOrder will be null, so we need to use the print state.
  const order = POSOrder || print?.order;
  const POSPayment = useSelector((state) =>
    print ? state.POSPayments.find((p) => p.order === print.id) : undefined,
  );
  // The transactionId is generated locally as soon as the payment is created,
  // well before the order has finished syncing to the backend, so it's the
  // most reliable identifier we have to find the synced order by.
  const transactionId = POSPayment?.meta.transactionId || print?.payment?.meta.transactionId;

  useEffect(() => {
    if (location.pathname.includes('settings')) {
      return;
    }

    if (!order) {
      return;
    }

    if (!print || !!order?.noReceipt) {
      logger.info('ReceiptPrinter: No print data found');
      return;
    }

    // If we've already printed this ID, close modal and return
    if (print?.id && printedIdsRef.current.includes(print.id)) {
      logger.info('ReceiptPrinter: Already printed this order, closing modal');
      askToPrintModal.hide();
      return;
    }

    // We skip printing if manualReceiptPrint is true
    if (storage.manualReceiptPrint) {
      logger.info('ReceiptPrinter: Skipping print due to manualReceiptPrint');
      return;
    }

    // If storageData.askToPrint is true, we prompt the user to print
    if (!printConfirmed) {
      askToPrintModal.toggle();
      return;
    }

    // Set the confirmed state back to false if we should prompt on every print
    storage.askToPrint && setPrintConfirmed(false);

    if (!print || !!POSOrder?.noReceipt || storage.manualReceiptPrint) {
      return;
    }

    printReceipt();

    printedIdsRef.current = _.compact([print?.id, ...printedIdsRef.current]).slice(
      0,
      MAX_PRINTED_IDS,
    );
  }, [print, order, printConfirmed]);

  const handleNoPrintClick = () => {
    logger.info('ReceiptPrinter: User declined to print');
    setPrintConfirmed(false);
    askToPrintModal.hide();
  };

  const handlePrintClick = () => {
    logger.info('ReceiptPrinter: User clicked yes to print');
    setPrintConfirmed(true);
    askToPrintModal.hide();
  };

  const handleMailClick = () => {
    logger.info('ReceiptPrinter: User clicked to email receipt');
    emailReceiptModal.toggle();
  };

  /**
   * The order in redux is the local (client-side) representation - it has no
   * real backend order id yet since it may not have finished syncing. To
   * email a receipt we need the real order id, so we search for the synced
   * order (by easyId + createdAt) and confirm it's the right one by matching
   * the transactionId we generated locally against the synced order's
   * payment.transactionId - the backend order id itself can't be used since
   * it's generated separately from the local order id.
   */
  const findSyncedOrderId = async (): Promise<string | null> => {
    if (!order || !transactionId) {
      return null;
    }

    for (let attempt = 0; attempt < FIND_ORDER_MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await sleep(FIND_ORDER_RETRY_DELAY_MS);
      }

      const { data } = await apolloClient.query<
        AdvancedSearchOrdersForEmail,
        AdvancedSearchOrdersForEmailVariables
      >({
        query: SEARCH_ORDERS_FOR_EMAIL_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            venueIds: [venueId],
            easyIds: order.orderNumber ? [order.orderNumber] : [],
            createdAt_gt: DateTime.fromJSDate(new Date(order.createdAt)).toISO(),
          },
        },
      });

      const searchedOrders = data?.operativeViewer.advancedSearchOrders.orders ?? [];

      const matchedOrder = searchedOrders.find(
        (searchedOrder) => searchedOrder.payment?.transactionId === transactionId,
      );

      if (matchedOrder) {
        return matchedOrder.id;
      }
    }

    return null;
  };

  const handleEmailSubmit = async (email: string) => {
    emailReceiptModal.hide();
    askToPrintModal.hide();

    dispatch(actions.addToast(i18n('TOAST.Email.Sending'), true));

    if (!order) {
      return;
    }

    logger.info('ReceiptPrinter: Looking up synced order to email receipt', {
      orderNumber: order.orderNumber,
    });

    const orderId = await findSyncedOrderId();

    if (!orderId) {
      logger.warn('ReceiptPrinter: Could not find synced order to email receipt', {
        orderNumber: order.orderNumber,
      });
      dispatch(actions.addToast(i18n('TOAST.Email.Not.Sent'), true));
      return;
    }

    logger.info('ReceiptPrinter: Sending order email', { orderId });

    const result = await api.sendOrderEmail({ orderId, email });

    if (!result.success || result.data?.sendOrderEmail?.error) {
      logger.warn('ReceiptPrinter: Failed to send order email', {
        orderId,
        msg: result.data?.sendOrderEmail?.message || result.error,
      });
      dispatch(actions.addToast(i18n('TOAST.Email.Not.Sent'), true));
    }
  };

  const printButton = useButton({
    onClick: handlePrintClick,
  });

  const mailButton = useButton({
    onClick: handleMailClick,
  });

  const noReceiptButton = useButton({
    onClick: handleNoPrintClick,
  });

  return (
    <>
      <Modal
        isShowing={askToPrintModal.isShowing && !location.pathname.includes('settings')}
        hide={askToPrintModal.hide}>
        <ModalWrapper
          header={{
            title: i18n('TM.WantToPrint'),
            icon: ReceiptIconSrc,
          }}>
          <Box display="flex" flexDirection="column" gap="medium" fluid>
            <Box fluid display="flex" gap="xs">
              <Button fluid {...noReceiptButton}>
                {i18n('General.No')}
              </Button>
              <BlueButton fluid {...printButton}>
                {i18n('General.Yes')}
              </BlueButton>
            </Box>

            <DividerWithText />

            <MailButton {...mailButton}>
              <Box display="flex" alignItems="center" justifyContent="center" gap="xs">
                <Icon
                  src={MailIconSrc}
                  color="var(--colorBlueGray200)"
                  width="20px"
                  height="20px"
                />
                {i18n('Receipt.Email')}
              </Box>
            </MailButton>
          </Box>
        </ModalWrapper>
      </Modal>

      <Modal isShowing={emailReceiptModal.isShowing} hide={emailReceiptModal.hide}>
        <EmailReceiptModal onSubmit={handleEmailSubmit} />
      </Modal>
    </>
  );
};

export default ReceiptPrinter;
